/**
 * "Styles your players can use" (ADR-037 D7): ship one of your Creative
 * Library styles with this arche. Its pictures are copied into the arche's
 * asset library (frozen with each published version, like every asset), and
 * the manifest needs a `styles` entry naming it — Claude adds that when asked.
 * Players then draw in the style (`{ shipped: key }`), paying for what they
 * make. The pictures become visible to every player, so nothing proprietary
 * may ship.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useHolderSwitcher } from '@/components/HolderSwitcher';
import { CreativeStudioLink } from '@/components/CreativeStudioLink';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const KEY_RE = /^[a-z0-9][a-z0-9-]{0,39}$/;

/** A key from a style's name: "Lit Mining!" → "lit-mining". */
export function styleKeyFrom(name: string): string {
  const key = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
  return KEY_RE.test(key) ? key : 'style';
}

/** What to ask Claude to add to fias-plugin.json. */
export function addStylePrompt(style: { key: string; name: string; words?: string }): string {
  return `I shipped my "${style.name}" style with this arche. Add it to the "styles" list in fias-plugin.json as ${JSON.stringify(style)}, and use it where players make art ({ shipped: "${style.key}" }).`;
}

interface ForgeShipStyleProps {
  archeId: string;
  /** The forge this page is: FiasWorks Studio opens here with a way back to it. */
  forgeId?: string;
  /** Ask Claude in a turn; absent while that is not possible. */
  onAskClaude?: (prompt: string) => void;
}

export function ForgeShipStyle({ archeId, forgeId, onAskClaude }: ForgeShipStyleProps) {
  const { selectedHolderId } = useHolderSwitcher();
  const holderId = selectedHolderId ?? null;
  const styles = useQuery({
    queryKey: queryKeys.vault.creative(holderId ?? '', 'style'),
    queryFn: async () => (await api.listCreativeItems(holderId!, 'style')).items,
    enabled: !!holderId,
    staleTime: 60_000,
  });
  const [styleId, setStyleId] = useState('');
  const [key, setKey] = useState('');
  const ship = useMutation({
    mutationFn: () => api.shipCreativeStyle(archeId, { styleId, key }),
  });

  const chosen = styles.data?.find((s) => s.itemId === styleId);
  useEffect(() => {
    if (chosen) setKey(styleKeyFrom(chosen.name));
  }, [chosen]);

  const items = styles.data ?? [];
  const validKey = KEY_RE.test(key);
  const shipped = ship.data?.style;

  return (
    <div className="space-y-2 rounded-lg border p-3" data-testid="forge-ship-style">
      <h3 className="text-sm font-medium">Styles your players can use</h3>
      <p className="text-xs text-muted-foreground">
        Ship one of your styles with this arche, and its players can make art in it — paying for
        what they make. Its pictures become visible to everyone who plays.
      </p>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {styles.isLoading ? (
            'Loading your styles…'
          ) : (
            <>
              You have no styles yet.{' '}
              <CreativeStudioLink
                {...(forgeId ? { from: { kind: 'forge' as const, id: forgeId } } : {})}
              >
                {(studio) => `Make one in ${studio}`}
              </CreativeStudioLink>
            </>
          )}
        </p>
      ) : (
        <div className="space-y-2">
          <select
            aria-label="Style to ship"
            className="h-8 w-full rounded-md border bg-background px-2 text-sm"
            value={styleId}
            onChange={(e) => setStyleId(e.target.value)}
          >
            <option value="">Pick a style…</option>
            {items.map((s) => (
              <option key={s.itemId} value={s.itemId}>
                {s.name}
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <Input
              aria-label="Key the arche uses for it"
              className="h-8 text-sm"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="key"
            />
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              disabled={!styleId || !validKey || ship.isPending}
              onClick={() => ship.mutate()}
            >
              {ship.isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Share2 className="mr-1 h-3.5 w-3.5" />
              )}
              Ship
            </Button>
          </div>
          {key && !validKey && (
            <p className="text-xs text-destructive">Lowercase letters, digits and dashes.</p>
          )}
        </div>
      )}
      {ship.error && (
        <p className="text-xs text-destructive" role="alert">
          {ship.error instanceof Error ? ship.error.message : 'The style was not shipped.'}
        </p>
      )}
      {shipped && (
        <div className="space-y-1.5 rounded-md bg-primary/5 p-2 text-xs" role="status">
          <p>
            Shipped “{shipped.name}” as <code>{shipped.key}</code> with {ship.data?.assets.length}{' '}
            picture{ship.data?.assets.length === 1 ? '' : 's'}. The manifest needs it in{' '}
            <code>styles</code> before the next publish.
          </p>
          {onAskClaude && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => onAskClaude(addStylePrompt(shipped))}
            >
              Ask Claude Code to add it
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
